import type { Ref } from "react";
import { Link } from "@tanstack/react-router";
import {
  formatLocation,
  formatPostedDate,
  formatSalaryPeriod,
} from "@/lib/utils";
import { trackSponsoredJobClick } from "@/hooks/useAnalytics";
import TagPill from "@/components/common/tagPill";
import type { JobWithRelations } from "@/types/db";
import { companyDisplayName } from "@/lib/jobLifecycle";

const GlobeIcon = () => (
  <svg
    className="size-3.5"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" />
    <path d="M2 12h20" />
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </svg>
);

interface JobCardProps {
  job: JobWithRelations;
  variant?: "home" | "compact";
  position?: number;
  /** Attach a callback ref for impression tracking. */
  ref?: Ref<HTMLAnchorElement>;
}

export default function JobCard({
  job,
  variant = "home",
  position,
  ref,
}: JobCardProps) {
  const posted = formatPostedDate(job.createdAt);
  const companyName = companyDisplayName(job);
  const location = formatLocation(job);
  const hasSalary =
    job.minSalary != null &&
    job.maxSalary != null &&
    Boolean(job.salaryCurrency && job.salaryPeriod);
  const hasAnyChip = job.isRemote || Boolean(location) || hasSalary;

  const chips = hasAnyChip ? (
    <div className="mt-3 flex flex-wrap items-center gap-2">
      {job.isRemote && (
        <TagPill
          variant="chip"
          bgColor="bg-blue-100 text-blue-800"
          leadingIcon={<GlobeIcon />}
        >
          Remote
        </TagPill>
      )}
      {location && (
        <TagPill variant="chip" bgColor="bg-orange-100 text-orange-800">
          {location}
        </TagPill>
      )}
      {hasSalary && (
        <TagPill variant="chip" bgColor="bg-emerald-100 text-emerald-800">
          {job.salaryCurrency ? `${job.salaryCurrency} ` : ""}
          {job.minSalary!.toLocaleString()}–{job.maxSalary!.toLocaleString()}
          {job.salaryPeriod ? ` / ${formatSalaryPeriod(job.salaryPeriod)}` : ""}
        </TagPill>
      )}
    </div>
  ) : null;

  // --- Compact variant ---
  if (variant === "compact") {
    return (
      <Link
        ref={ref}
        data-impression-slug={job.slug}
        to="/$slug"
        params={{ slug: job.slug ?? "" }}
        className="group flex flex-col rounded-xl border border-gray-200 bg-white p-5 shadow-sm transition-all duration-200 hover:border-blue-300 hover:shadow-md sm:p-6"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-gray-400">
              {companyName}
            </p>
            <h3 className="mt-1 line-clamp-2 text-lg font-semibold tracking-tight text-gray-900 sm:text-xl">
              {job.role}
            </h3>
          </div>
          <div className="shrink-0 pt-1">
            {posted.isNew ? (
              <span className="text-sm font-semibold text-violet-600">New</span>
            ) : (
              <span className={`text-sm ${posted.colorClass}`}>
                {posted.label}
              </span>
            )}
          </div>
        </div>
        {chips}
      </Link>
    );
  }

  // --- Home (Sponsored) variant ---
  return (
    <Link
      ref={ref}
      data-impression-slug={job.slug}
      to="/$slug"
      params={{ slug: job.slug ?? "" }}
      state={{ from: "home" } as never}
      onClick={() => {
        if (job.slug && position !== undefined) {
          trackSponsoredJobClick(job.slug, position);
        }
      }}
      className="group flex flex-col rounded-xl border border-amber-100 bg-amber-50/40 p-5 shadow-sm transition-all duration-200 hover:border-amber-200 hover:bg-amber-50 hover:shadow-md sm:p-6"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <p className="mb-1.5 font-mono text-[0.6875rem] font-semibold tracking-wide text-amber-700 uppercase">
            Highlighted
          </p>
          <p className="truncate text-sm font-medium text-gray-400">
            {companyName}
          </p>
          <h3 className="mt-1 line-clamp-2 text-lg font-semibold tracking-tight text-gray-900 sm:text-xl">
            {job.role}
          </h3>
        </div>
        {posted.isNew && (
          <span className="shrink-0 pt-1 text-sm font-semibold text-violet-600">
            New
          </span>
        )}
      </div>
      {chips}
    </Link>
  );
}
